// Step 6 — Connect Cloudflare (go live).
//
// The one unavoidable manual step: connecting a CF Pages project to the
// public repo is an OAuth *app installation*, which no API can do
// headlessly (not a BKA limitation — nobody can automate it). So this
// panel is a guided checklist, not an automated call: a deep link to
// CF's "Connect to Git", the exact build settings to paste (no build
// command — the repo is already finished static HTML), and a liveness
// check. The creator's CF token never enters BKA.
//
// Because the starter site was already committed (Step 5), the instant
// they finish connecting, real content deploys. On go-live the wizard
// completes and the creator drops into the app.

import { useState } from "react";
import { useAuthStore } from "../../store/authStore";
import { useOnboardingStore } from "../../store/onboardingStore";
import { useNavStore } from "../../store/navStore";
import { defaultSiteUrl, publicRepoName } from "../../lib/handle";

const CF_CONNECT_URL = "https://dash.cloudflare.com/?to=/:account/workers-and-pages/create/pages";

function Copy({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="cf-copy"
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        });
      }}
    >
      {copied ? "copied" : "copy"}
    </button>
  );
}

function Setting({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="cf-setting">
      <span className="cf-setting-label">{label}</span>
      <code className="cf-setting-value">{value || "(leave empty)"}</code>
      {value && <Copy value={value} />}
      {hint && <span className="cf-setting-hint">{hint}</span>}
    </div>
  );
}

export function StepCloudflare() {
  const user = useAuthStore((s) => s.user)!;
  const handle = useOnboardingStore((s) => s.record?.handle) ?? "";
  const siteUrl = useOnboardingStore((s) => s.record?.cf_pages_project) ?? defaultSiteUrl(handle);
  const finishOnboarding = useOnboardingStore((s) => s.finishOnboarding);
  const setView = useNavStore((s) => s.setView);

  const [checking, setChecking] = useState(false);
  const [live, setLive] = useState(false);
  const [checkedOnce, setCheckedOnce] = useState(false);

  const projectName = publicRepoName(handle);

  const checkLive = async () => {
    setChecking(true);
    setCheckedOnce(true);
    // no-cors probe: resolves (opaque) if the subdomain responds at all,
    // rejects on DNS/connection failure (not deployed yet). Good-enough
    // liveness without CORS, which CF Pages static assets don't send.
    try {
      await fetch(siteUrl + "/", { mode: "no-cors", cache: "no-store" });
      setLive(true);
    } catch {
      setLive(false);
    } finally {
      setChecking(false);
    }
  };

  const finish = async () => {
    await finishOnboarding(user.sub);
    setView("app");
  };

  if (live) {
    return (
      <div className="step">
        <div className="step-kicker">Setup complete</div>
        <h1 className="step-title">Your site is live 🎉</h1>
        <p className="step-why">
          You went from “I opened BKA” to a published post on infrastructure you own — no platform
          in the middle. Bookmark your site. From here on, the wizard is done: open BKA and you
          land straight in the composer.
        </p>
        <a className="cf-livelink" href={siteUrl} target="_blank" rel="noreferrer">
          {siteUrl.replace("https://", "")} ↗
        </a>
        <div className="step-actions">
          <button className="step-btn" onClick={() => void finish()}>Enter BKA →</button>
        </div>
        <Styles />
      </div>
    );
  }

  return (
    <div className="step">
      <div className="step-kicker">Step 6 of 7</div>
      <h1 className="step-title">Connect Cloudflare</h1>
      <p className="step-why">
        One-time setup: point Cloudflare Pages at your public repo. After this, every time you
        publish, your site updates on its own — BKA just pushes to GitHub and Cloudflare deploys
        it. We never touch your Cloudflare account; <em>you</em> own it.
      </p>

      <ol className="cf-steps">
        <li>
          <a href={CF_CONNECT_URL} target="_blank" rel="noreferrer" className="step-btn step-btn-ghost cf-open">
            Open Cloudflare Pages ↗
          </a>
          <div className="step-hint">Workers &amp; Pages → Create → <strong>Pages</strong> tab → <strong>Connect to Git</strong>. Authorize Cloudflare for your GitHub account if asked.</div>
        </li>
        <li>
          <div className="step-hint">Pick the repo <code>{projectName}</code> and use these settings:</div>
          <div className="cf-settings">
            <Setting label="Project name" value={projectName} hint="gives you the URL below" />
            <Setting label="Production branch" value="main" />
            <Setting label="Framework preset" value="None" />
            <Setting label="Build command" value="" hint="your repo is already built — leave blank" />
            <Setting label="Build output directory" value="/" />
          </div>
        </li>
        <li>
          <div className="step-hint">Click <strong>Save and Deploy</strong>. First deploy takes ~30s. Your site will be at:</div>
          <code className="cf-url">{siteUrl.replace("https://", "")}</code>
        </li>
      </ol>

      <div className="step-actions">
        <button className="step-btn" disabled={checking} onClick={() => void checkLive()}>
          {checking ? "Checking…" : "Check if my site is live"}
        </button>
        <a className="step-link" href={siteUrl} target="_blank" rel="noreferrer">open my site ↗</a>
      </div>

      {checkedOnce && !live && !checking && (
        <div className="cf-notlive">
          Not reachable yet. The first deploy can take a minute — give it a moment and check again,
          or open your site to confirm. If you can see it in your browser, you can{" "}
          <button className="step-link" onClick={() => void finish()}>finish setup anyway →</button>.
        </div>
      )}

      <Styles />
    </div>
  );
}

function Styles() {
  return (
    <style>{`
      .cf-steps { list-style: decimal; margin: 0; padding-left: 20px; display: flex; flex-direction: column; gap: var(--space-4); }
      .cf-steps > li { padding-left: 4px; }
      .cf-open { display: inline-block; width: fit-content; text-decoration: none; margin-bottom: 6px; }
      .cf-settings {
        margin-top: var(--space-3);
        background: var(--paper-raised); border: 1px solid var(--line);
        border-radius: var(--radius-1); overflow: hidden;
      }
      .cf-setting {
        display: grid;
        grid-template-columns: 150px 1fr auto;
        align-items: center; gap: var(--space-2);
        padding: 8px 12px;
        border-bottom: 1px solid var(--line);
      }
      .cf-setting:last-child { border-bottom: none; }
      .cf-setting-label { font-size: 12px; color: var(--ink-soft); font-weight: 600; }
      .cf-setting-value { font-family: var(--font-mono); font-size: 13px; color: var(--ink); }
      .cf-setting-hint { grid-column: 2 / 4; font-size: 11px; color: var(--ink-faint); }
      .cf-copy {
        font-family: var(--font-mono); font-size: 10px; text-transform: uppercase;
        letter-spacing: 0.06em; color: var(--teal);
        background: var(--teal-dim); border: none; border-radius: 6px;
        padding: 3px 8px; cursor: pointer;
      }
      .cf-url, .cf-livelink {
        display: inline-block; font-family: var(--font-mono); font-size: 14px;
        color: var(--teal); margin-top: 6px;
      }
      .cf-livelink {
        font-size: 16px; margin: var(--space-2) 0 var(--space-4);
        padding: var(--space-3) var(--space-4);
        background: var(--teal-dim); border-radius: var(--radius-1);
      }
      .cf-notlive {
        margin-top: var(--space-4); padding: var(--space-3);
        background: var(--paper-sunk); border-radius: var(--radius-1);
        font-size: 13px; line-height: 1.55; color: var(--ink-soft);
      }
    `}</style>
  );
}
